import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useCart, isOutOfStock } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useBestSellerIds } from "@/context/BestSellerContext";
import { BACKEND_URL } from "@/lib/api";

// Closed vocabulary for shopper-facing tag chips (matches backend product_tagger.TAG_VOCAB).
const CHIP_TAGS = new Set([
  "beginner-friendly", "luxury", "travel-size", "couples", "discreet",
  "gift-ready", "waterproof", "whisper-quiet", "rechargeable", "remote-control",
  "hands-free", "silicone", "leather", "vegan",
]);

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist() || {};
  const saved = has?.(product.product_id);
  const oos = isOutOfStock(product);
  const inv = product.inventory_count;
  const low = !oos && product.product_type !== "ai_companion" && inv != null && inv <= 5;
  const bestSellerIds = useBestSellerIds();
  const isBestSeller = bestSellerIds.has(product.product_id);
  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col border"
      style={{
        background: "var(--rs-surface)",
        borderColor: "var(--rs-border)",
      }}
    >
      {isBestSeller && !oos && (
        <div
          data-testid={`bestseller-badge-${product.slug}`}
          className="absolute top-3 right-12 z-10 px-2.5 py-1 text-[9px] uppercase tracking-[0.22em] flex items-center gap-1"
          style={{
            background: "linear-gradient(135deg, #d4af37 0%, #b8912d 100%)",
            color: "#0a0a0a",
            fontWeight: 600,
            letterSpacing: "0.15em",
            boxShadow: "0 2px 8px rgba(212,175,55,0.25)",
          }}
          title="A house best seller — top-selling in the last 30 days"
        >
          ★ Best Seller
        </div>
      )}
      {(oos || low) && (
        <div
          data-testid={`stock-badge-${product.slug}`}
          className="absolute top-3 left-3 z-10 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em]"
          style={
            oos
              ? { background: "var(--rs-oxblood)", color: "var(--rs-bone, #f5f2eb)" }
              : { background: "rgba(10,10,10,0.7)", color: "#d4af37", border: "1px solid rgba(212,175,55,0.4)" }
          }
        >
          {oos ? "Out of stock" : `Only ${inv} left`}
        </div>
      )}
      <button
        data-testid={`wishlist-toggle-${product.slug}`}
        onClick={() => toggle?.(product.product_id)}
        className="absolute top-3 right-3 z-10 p-2 transition"
        style={{ background: "rgba(10,10,10,0.55)" }}
        aria-label={saved ? "Remove from Chambers" : "Save to Chambers"}
      >
        <Heart
          strokeWidth={1.5}
          size={16}
          className={saved ? "text-bone" : "text-muted-bone hover:text-bone"}
          fill={saved ? "var(--rs-oxblood)" : "none"}
        />
      </button>
      <Link
        data-testid={`product-link-${product.slug}`}
        to={`/product/${product.slug}`}
        className="block aspect-[3/4] overflow-hidden relative"
      >
        <img
          src={resolveImg(product.image)}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          style={oos ? { filter: "grayscale(0.7)", opacity: 0.6 } : undefined}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </Link>

      <div className="p-6 flex flex-col gap-2">
        <div className="overline">{product.category}</div>
        <Link
          to={`/product/${product.slug}`}
          className="font-serif-display text-lg sm:text-xl text-bone leading-snug hover:underline underline-offset-4 decoration-1"
        >
          {product.name}
        </Link>
        {product.tags && product.tags.length > 0 && (
          <div
            data-testid={`product-tags-${product.slug}`}
            className="flex flex-wrap gap-1.5 mt-1"
          >
            {product.tags
              .filter((t) => typeof t === "string" && CHIP_TAGS.has(t))
              .slice(0, 3)
              .map((t) => (
                <span
                  key={t}
                  className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5"
                  style={{
                    color: "var(--rs-terracotta, #c07050)",
                    border: "1px solid rgba(192,112,80,0.35)",
                    background: "rgba(192,112,80,0.06)",
                  }}
                >
                  {t}
                </span>
              ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="text-bone text-base">${product.price.toFixed(2)}</span>
          <button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={() => addItem(product)}
            disabled={oos}
            className="text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-muted-bone"
          >
            {oos ? "Out of stock" : "Add — to bag"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
